import type { ConfiguratorConfig, BootstrapData, LumaSubcategory, LumaSize } from './types';
import { isConfiguratorUnavailablePayload } from './types';
import { ApiService } from './ApiService';
import { StepManager } from './StepManager';
import { PriceDisplay } from './PriceDisplay';
import { CartSubmitter } from './CartSubmitter';

/**
 * Main controller for the Lumaprints Product Configurator block.
 *
 * Lifecycle:
 *   1. Reads bootstrap data from the block's data attributes (injected by PHP)
 *   2. Fetches the full configurator config from /luma/v1/configurator/config
 *   3. Mounts the StepManager wizard into the block root
 *   4. On completion of all steps, fires a pricing request and shows the live price
 *   5. On "Add to Cart", validates selections and submits to /luma/v1/cart/add
 */
export class Configurator {

	private blockRoot: HTMLElement;
	private inner: HTMLElement;
	private api: ApiService;
	private stepManager: StepManager | null = null;
	private priceDisplay: PriceDisplay | null = null;
	private cartSubmitter: CartSubmitter | null = null;
	private bootstrap: BootstrapData;
	private config: ConfiguratorConfig | null = null;

	private submitOverlayEl: HTMLElement | null = null;

	// Debounce timer for pricing requests.
	private priceDebounceTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly PRICE_DEBOUNCE_MS = 400;

	constructor(blockRoot: HTMLElement) {
		this.blockRoot = blockRoot;

		const innerEl = blockRoot.querySelector<HTMLElement>('.luma-configurator__inner');

		if (!innerEl) {
			throw new Error('Configurator: could not find .luma-configurator__inner');
		}

		this.inner = innerEl;

		const rawBootstrap = innerEl.dataset['bootstrap'] ?? '';
		this.bootstrap     = JSON.parse(rawBootstrap) as BootstrapData;

		this.api = new ApiService(this.bootstrap.restUrl, this.bootstrap.restNonce);
	}

	async init(): Promise<void> {
		if (this.bootstrap.available === false) {
			return;
		}

		try {
			const result = await this.api.fetchConfig(this.bootstrap.productId);

			if (isConfiguratorUnavailablePayload(result)) {
				this.showUnavailableMessage(result.message);
				return;
			}

			this.config = result;
		} catch (err) {
			this.showError('Could not load product configuration. Please refresh the page.');
			console.error('[Lumaprints Configurator] Config fetch error:', err);
			return;
		}

		this.mountUI();
	}

	private mountUI(): void {
		if (!this.config) return;

		this.inner.innerHTML = '';

		// Price display container (hidden until all selections are made).
		const priceContainer = document.createElement('div');
		priceContainer.className = 'luma-configurator__price';
		priceContainer.setAttribute('aria-live', 'polite');

		const priceMain = document.createElement('div');
		priceMain.className = 'luma-configurator__price-main';

		const priceLabel = document.createElement('span');
		priceLabel.className = 'luma-configurator__price-label';
		priceLabel.textContent = 'Price: ';

		const priceValue = document.createElement('span');
		priceValue.className = 'luma-configurator__price-value';

		const priceSpinner = document.createElement('span');
		priceSpinner.className = 'luma-configurator__price-spinner';
		priceSpinner.setAttribute('aria-hidden', 'true');

		const priceDetail = document.createElement('span');
		priceDetail.className = 'luma-configurator__price-detail';
		priceDetail.hidden    = true;

		priceMain.appendChild(priceLabel);
		priceMain.appendChild(priceValue);
		priceMain.appendChild(priceSpinner);
		priceContainer.appendChild(priceMain);
		priceContainer.appendChild(priceDetail);
		this.inner.appendChild(priceContainer);

		this.priceDisplay = new PriceDisplay(priceContainer);
		this.priceDisplay.showHint('Select all options to see your price');

		// Steps container.
		const stepsContainer = document.createElement('div');
		stepsContainer.className = 'luma-configurator__steps';
		this.inner.appendChild(stepsContainer);

		// Add-to-cart area.
		const cartArea = document.createElement('div');
		cartArea.className = 'luma-configurator__cart-area';
		cartArea.hidden    = true;
		this.inner.appendChild(cartArea);

		// Initialise helpers.
		this.cartSubmitter = new CartSubmitter(this.config.addToCartNonce ?? this.bootstrap.addToCartNonce);
		this.stepManager   = new StepManager(stepsContainer, this.config, this.bootstrap.productName);

		this.stepManager.onSelectionChange = () => {
			cartArea.innerHTML = '';
			cartArea.hidden    = true;
			cartArea.removeAttribute('aria-busy');
			this.priceDisplay?.showHint('Select all options to see your price');
		};

		this.stepManager.onAllSelectionsComplete = (subcat, size, options) => {
			this.onAllSelectionsComplete(subcat, size, options, cartArea);
		};

		this.stepManager.render();
	}

	private onAllSelectionsComplete(
		subcat: LumaSubcategory,
		size: LumaSize,
		options: Record<string, number>,
		cartArea: HTMLElement,
	): void {
		if (!this.priceDisplay) return;

		this.priceDisplay.showLoading();
		this.showCartAreaLoading(cartArea);

		// Debounce pricing requests to avoid firing on rapid option changes.
		if (this.priceDebounceTimer !== null) {
			clearTimeout(this.priceDebounceTimer);
		}

		this.priceDebounceTimer = setTimeout(async () => {
			await this.fetchAndDisplayPrice(subcat, size, options, cartArea);
		}, this.PRICE_DEBOUNCE_MS);
	}

	private async fetchAndDisplayPrice(
		subcat: LumaSubcategory,
		size: LumaSize,
		options: Record<string, number>,
		cartArea: HTMLElement,
	): Promise<void> {
		try {
			const optionIds = Object.values(options);

			const pricing = await this.api.fetchPrice({
				subcategoryId: subcat.subcategoryId,
				size,
				options:       optionIds,
			});

			const panelCount = Math.max(1, this.config?.productionImageCount ?? 1);
			this.priceDisplay?.showPrice(pricing.displayedPrice, panelCount);
			this.renderCartArea(cartArea, subcat, size, options, pricing.basePrice, pricing.displayedPrice);
		} catch (err) {
			this.priceDisplay?.showError('Price unavailable. Please try again.');
			cartArea.innerHTML = '';
			cartArea.hidden    = true;
			cartArea.removeAttribute('aria-busy');
			console.error('[Lumaprints Configurator] Pricing error:', err);
		}
	}

	/**
	 * Placeholder spinner where the Add to Cart button will appear while price loads.
	 */
	private showCartAreaLoading(cartArea: HTMLElement): void {
		cartArea.innerHTML = '';
		cartArea.hidden    = false;
		cartArea.setAttribute('aria-busy', 'true');

		const wrap = document.createElement('div');
		wrap.className = 'luma-configurator__cart-loading';
		wrap.setAttribute('role', 'status');
		wrap.setAttribute('aria-live', 'polite');

		const spinner = document.createElement('span');
		spinner.className = 'luma-configurator__spinner';
		spinner.setAttribute('aria-hidden', 'true');

		const label = document.createElement('span');
		label.className = 'luma-configurator__cart-loading-label';
		label.textContent = 'Preparing add to cart…';

		wrap.appendChild(spinner);
		wrap.appendChild(label);
		cartArea.appendChild(wrap);
	}

	private renderCartArea(
		cartArea: HTMLElement,
		subcat: LumaSubcategory,
		size: LumaSize,
		options: Record<string, number>,
		basePrice: number,
		displayedPrice: number,
	): void {
		cartArea.innerHTML = '';
		cartArea.hidden    = false;
		cartArea.removeAttribute('aria-busy');

		const errorMsg = document.createElement('p');
		errorMsg.className = 'luma-configurator__cart-error';
		errorMsg.hidden    = true;
		cartArea.appendChild(errorMsg);

		const btn = document.createElement('button');
		btn.type      = 'button';
		btn.className = 'luma-configurator__add-to-cart-btn';
		btn.textContent = 'Add to Cart';

		btn.addEventListener('click', () => {
			void (async () => {
				if (!this.stepManager || !this.cartSubmitter) return;

				const validationError = this.cartSubmitter.validate(
					this.bootstrap.productId,
					subcat,
					size,
					options,
				);

				if (validationError) {
					errorMsg.textContent = validationError;
					errorMsg.hidden      = false;
					return;
				}

				btn.disabled    = true;
				btn.textContent = 'Adding…';
				errorMsg.hidden = true;

				this.showSubmitOverlay();

				try {
					await this.cartSubmitter.submit({
						productId:            this.bootstrap.productId,
						subcat,
						size,
						selectedOptions:      options,
						basePrice,
						displayedPrice,
						productionImageCount: this.config?.productionImageCount ?? 1,
					});
				} catch (err) {
					this.hideSubmitOverlay();
					btn.disabled    = false;
					btn.textContent = 'Add to Cart';
					errorMsg.textContent = 'Could not add to cart. Please try again.';
					errorMsg.hidden      = false;
					console.error('[Lumaprints Configurator] Add to cart error:', err);
				}
			})();
		});

		cartArea.appendChild(btn);
		cartArea.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
	}

	private showError(message: string): void {
		this.inner.innerHTML = '';
		const errEl = document.createElement('p');
		errEl.className   = 'luma-configurator__error';
		errEl.textContent = message;
		this.inner.appendChild(errEl);
	}

	private showUnavailableMessage(message: string): void {
		this.inner.innerHTML = '';
		const p = document.createElement('p');
		p.className   = 'luma-configurator__unavailable';
		p.textContent = message;
		this.inner.appendChild(p);
	}

	private ensureSubmitOverlay(): HTMLElement {
		if (this.submitOverlayEl) {
			return this.submitOverlayEl;
		}

		const overlay = document.createElement('div');
		overlay.className = 'luma-configurator__submit-overlay';
		overlay.hidden    = true;
		overlay.setAttribute('aria-hidden', 'true');

		const panel = document.createElement('div');
		panel.className = 'luma-configurator__submit-overlay-panel';
		panel.setAttribute('role', 'status');
		panel.setAttribute('aria-live', 'polite');

		const spinner = document.createElement('span');
		spinner.className = 'luma-configurator__spinner';
		spinner.setAttribute('aria-hidden', 'true');

		const title = document.createElement('span');
		title.className = 'luma-configurator__submit-overlay-title';
		title.textContent = 'Adding to cart…';

		const hint = document.createElement('span');
		hint.className = 'luma-configurator__submit-overlay-hint';
		hint.textContent = 'Please do not refresh or leave this page until we are done.';

		panel.appendChild(spinner);
		panel.appendChild(title);
		panel.appendChild(hint);
		overlay.appendChild(panel);

		this.blockRoot.appendChild(overlay);
		this.submitOverlayEl = overlay;

		return overlay;
	}

	private showSubmitOverlay(): void {
		const overlay = this.ensureSubmitOverlay();
		overlay.hidden = false;
		overlay.setAttribute('aria-hidden', 'false');
		this.blockRoot.setAttribute('aria-busy', 'true');
	}

	private hideSubmitOverlay(): void {
		if (!this.submitOverlayEl) return;

		this.submitOverlayEl.hidden = true;
		this.submitOverlayEl.setAttribute('aria-hidden', 'true');
		this.blockRoot.removeAttribute('aria-busy');
	}
}
